import { useEffect, useId, useRef, useState } from 'react';
import type { ProgramPackage } from './packages';
import { useT } from '../i18n/i18n';

type Props = {
  open: boolean;
  title: string;
  message: string;
  packages: ProgramPackage[];
  allowCreate: boolean;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  onPick: (packageId: string) => void;
  onCreate: (name: string) => void;
  onCancel: () => void;
};

export default function PackagePicker({
  open,
  title,
  message,
  packages,
  allowCreate,
  confirmLabel,
  busy,
  error,
  onPick,
  onCreate,
  onCancel,
}: Props) {
  const t = useT();
  const titleId = useId();
  const descId = useId();
  const newNameId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [chosen, setChosen] = useState<string>('');
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    setChosen(packages.length > 0 ? packages[0].id : '');
    setNewName('');
  }, [open, packages]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const making = allowCreate && newName.trim().length > 0;
  const canConfirm = !busy && (making || chosen !== '');

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <h2 id={titleId} className="modal-title">{title}</h2>
        <p id={descId} className="modal-body">{message}</p>

        {error && <div className="error" role="alert">{error}</div>}

        {packages.length === 0 ? (
          <p className="muted">
            {allowCreate
              ? t('There are no sets yet. Name one below and it will be made.')
              : t('There are no sets yet. Put a program into one from its own row first.')}
          </p>
        ) : (
          <div className="field">
            <span className="field-label">{t('Choose a set')}</span>
            <div role="radiogroup" aria-label={t('Sets')}>
              {packages.map((p) => (
                <label className="check" key={p.id}>
                  <input
                    type="radio"
                    name="package-choice"
                    value={p.id}
                    checked={chosen === p.id && !making}
                    disabled={busy || making}
                    onChange={() => setChosen(p.id)}
                  />
                  {p.name}
                  <span className="field-hint">
                    {' '}
                    {p.programCount === 1 ? t('1 program') : t('{n} programs', { n: p.programCount })}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {allowCreate && (
          <div className="field">
            <label htmlFor={newNameId}>{t('Or make a new set')}</label>
            <input
              id={newNameId}
              value={newName}
              maxLength={120}
              placeholder={t('Starting set')}
              disabled={busy}
              onChange={(e) => setNewName(e.target.value)}
            />
            <span className="field-hint">
              {t('Typing a name here makes a new set instead of using the one chosen above.')}
            </span>
          </div>
        )}

        <div className="modal-actions">
          <button
            ref={cancelRef}
            type="button"
            className="btn secondary"
            disabled={busy}
            onClick={onCancel}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className="btn"
            disabled={!canConfirm}
            onClick={() => { if (making) onCreate(newName.trim()); else onPick(chosen); }}
          >
            {busy ? t('Working…') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
